import { ContentWorkflow } from '@amzn/awscat-pattern-library-service-model';
import {
  Box,
  Button,
  Cards,
  ExpandableSection,
  FormField,
  Link,
  Pagination,
  SpaceBetween,
  StatusIndicator,
  Textarea,
} from '@amzn/awsui-components-core';
import { DateTime } from 'luxon';
import arraySort from 'array-sort';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import constants from '../../common/constants';
import { AppContext } from '../../contexts/AppContext';
import { extractUserAlias, tablePaginationAriaLabels } from '../../api/UtilsApi';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { IMplServiceClient } from '../../api/IMplServiceClient';

interface Props {
  workflow: ContentWorkflow;
  showAllNotes?: boolean;
}

interface ContentNoteItem {
  createdAt: string;
  contentId: string;
  deletable: boolean;
  editable: boolean;
  error?: string;
  index: number;
  lastUpdatedAt?: DateTime;
  noteId: string;
  notes: string;
  originalNotes?: string;
  userId: string;
}

const MAX_NOTES_DISPLAY = 1;
const CARD_PAGE_SIZE = 15;

const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();

const ContentNotesDetails = ({ showAllNotes, workflow }: Props) => {
  const { contentId } = workflow;
  const [isLoading, setIsLoading] = useState(true);
  const [notes, setNotes] = useState<ContentNoteItem[]>([]);
  const [showNewNote, setShowNewNote] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newNoteError, setNewNoteError] = useState('');
  const appContext = useContext(AppContext);
  // @ts-expect-error TS18048 - strict migration
  const { userId } = appContext.authnInfo.userProfile;

  useEffect(() => {
    const fetchNotes = async () => {
      setIsLoading(true);
      try {
        const { contentNotes } = await mplServiceClient.listContentNotes(contentId);
        arraySort(contentNotes, 'createdAt', { reverse: true });
        const updatedNotes = contentNotes.map((contentNote, index) => ({
          ...contentNote,
          createdAt: contentNote.createdAt.toLocaleString(DateTime.DATE_FULL),
          index,
          editable: false,
          deletable: false,
          error: '',
        }));
        setNotes(updatedNotes);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotes();
  }, [contentId]);

  const addNote = () => {
    setNewNote('');
    setShowNewNote(true);
  };

  const changeNoteEditMode = (note: ContentNoteItem, canEdit: boolean) => {
    const updatedNotes = [...notes];
    // @ts-expect-error TS2532 - strict migration
    updatedNotes[note.index].editable = canEdit;
    if (canEdit) {
      // @ts-expect-error TS2532 - strict migration
      updatedNotes[note.index].originalNotes = updatedNotes[note.index].notes;
    } else {
      // @ts-expect-error TS2322 - strict migration
      updatedNotes[note.index].notes = updatedNotes[note.index].originalNotes;
    }
    setNotes(updatedNotes);
  };

  const changeNoteDeleteMode = (note: ContentNoteItem, canDelete: boolean) => {
    const updatedNotes = [...notes];
    // @ts-expect-error TS2532 - strict migration
    updatedNotes[note.index].deletable = canDelete;
    setNotes(updatedNotes);
  };

  const updateNote = async (note: ContentNoteItem) => {
    await mplServiceClient.updateContentNote(contentId, note.noteId, note.notes);
    const updatedNotes = [...notes];
    // @ts-expect-error TS2532 - strict migration
    updatedNotes[note.index].editable = false;
    setNotes(updatedNotes);
  };

  const saveNewNote = async () => {
    const { contentNote } = await mplServiceClient.createContentNote(contentId, newNote);
    const createdNote = {
      ...contentNote,
      createdAt: contentNote.createdAt.toLocaleString(DateTime.DATE_FULL),
      index: 0,
      editable: false,
      deletable: false,
      error: '',
    };
    const updatedNotes = [createdNote, ...notes];
    updatedNotes.forEach((item, index) => (item.index = index));
    setNotes(updatedNotes);
    setShowNewNote(false);
  };

  const deleteNote = async (note: ContentNoteItem) => {
    await mplServiceClient.deleteContentNote(contentId, note.noteId);
    const updatedNotes = [...notes];
    updatedNotes.splice(note.index, 1);
    updatedNotes.forEach((item, index) => (item.index = index));
    setNotes(updatedNotes);
  };

  const handleNoteChange = (changedNote: string, note?: ContentNoteItem) => {
    let updatedNote = changedNote;
    let error = '';
    if (updatedNote.length > constants.NOTE_LENGTH_LIMIT) {
      updatedNote = updatedNote.slice(0, constants.NOTE_LENGTH_LIMIT);
      error = constants.NOTE_LENGTH_LIMIT_ERROR;
    }
    if (note) {
      const updatedNotes = [...notes];
      // @ts-expect-error TS2532 - strict migration
      updatedNotes[note.index].notes = updatedNote;
      // @ts-expect-error TS2532 - strict migration
      updatedNotes[note.index].error = error;
      setNotes(updatedNotes);
    } else {
      setNewNote(updatedNote);
      setNewNoteError(error);
    }
  };

  const emptyTemplate = (
    <Box textAlign="center">
      <b>No notes to display</b>
      <Box padding={{ top: 's', bottom: 's' }} variant="p">
        <Button onClick={addNote}>Create note</Button>
      </Box>
    </Box>
  );

  const showNoteContent = (note: ContentNoteItem) => {
    return (
      <SpaceBetween direction="vertical" size="xs">
        <FormField errorText={note.error} stretch i18nStrings={{ errorIconAriaLabel: 'Error' }}>
          {note.editable ? (
            <Textarea
              autoFocus
              onChange={({ detail }) => handleNoteChange(detail.value, note)}
              value={note.notes}
              rows={3}
              ariaRequired
            />
          ) : note.notes.length > constants.NOTE_DISPLAY_LENGTH_LIMIT ? (
            <Textarea disabled value={note.notes} rows={3} ariaRequired />
          ) : (
            note.notes
          )}
        </FormField>
      </SpaceBetween>
    );
  };

  const showEditControls = (note: ContentNoteItem) => (
    <span className="float-right">
      <SpaceBetween direction="horizontal" size="xs">
        <Button onClick={() => updateNote(note)}>Save</Button>
        <Button onClick={() => changeNoteEditMode(note, false)}>Cancel</Button>
      </SpaceBetween>
    </span>
  );

  const showDeleteControls = (note: ContentNoteItem) => (
    <span className="float-right">
      <SpaceBetween direction="horizontal" size="xs">
        <Button onClick={() => deleteNote(note)}>Delete</Button>
        <Button onClick={() => changeNoteDeleteMode(note, false)}>Cancel</Button>
      </SpaceBetween>
    </span>
  );

  const showNoteAuthor = (note: ContentNoteItem) => {
    return (
      <div className="card-body-author-section">
        {extractUserAlias(note.userId)} | {note.createdAt}
        {note.userId === userId &&
          (note.editable ? (
            showEditControls(note)
          ) : note.deletable ? (
            showDeleteControls(note)
          ) : (
            <span className="float-right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  iconUrl={'/images/icon-edit.svg'}
                  variant="icon"
                  ariaLabel="Edit note"
                  iconAlt="Edit"
                  onClick={() => changeNoteEditMode(note, true)}
                />
                <Button
                  iconUrl={'/images/icon-delete.svg'}
                  variant="icon"
                  ariaLabel="Delete note"
                  iconAlt="Delete"
                  onClick={() => changeNoteDeleteMode(note, true)}
                />
              </SpaceBetween>
            </span>
          ))}
      </div>
    );
  };

  const cardDefinition = {
    // @ts-expect-error TS7006 - strict migration
    header: (note) => showNoteContent(note),
    sections: [
      {
        id: 'userId',
        // @ts-expect-error TS7006 - strict migration
        content: (note) => showNoteAuthor(note),
      },
    ],
  };

  const newNoteElement = (
    <Box padding="xs" className="content-new-notes">
      <SpaceBetween direction="vertical" size="xs">
        <FormField errorText={newNoteError} stretch i18nStrings={{ errorIconAriaLabel: 'Error' }}>
          <Textarea
            onChange={({ detail }) => handleNoteChange(detail.value)}
            value={newNote}
            autoFocus
            rows={3}
            ariaRequired
          />
        </FormField>
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={saveNewNote}>Save</Button>
            <Button onClick={() => setShowNewNote(false)}>Cancel</Button>
          </SpaceBetween>
        </Box>
      </SpaceBetween>
    </Box>
  );

  const addNewNoteElement = <Button onClick={addNote}>Create note</Button>;

  const moreNotesElement = (
    <Link variant="secondary" href={`/tech-writer/content-notes/${contentId}`} target="_blank">
      ... More notes
    </Link>
  );

  const { items, collectionProps, paginationProps } = useCollection(notes, {
    pagination: { pageSize: CARD_PAGE_SIZE },
  });

  if (isLoading) {
    return <StatusIndicator type="loading">Loading...</StatusIndicator>;
  }

  return (
    <div>
      <ExpandableSection header="Notes" variant="footer" defaultExpanded>
        <Cards
          {...collectionProps}
          cardsPerRow={[{ cards: 1 }]}
          cardDefinition={cardDefinition}
          items={showAllNotes ? items : !showNewNote ? items.slice(0, MAX_NOTES_DISPLAY) : []}
          empty={!showNewNote && emptyTemplate}
          pagination={
            showAllNotes && (
              <Pagination
                {...paginationProps}
                ariaLabels={tablePaginationAriaLabels}
                disabled={isLoading}
              />
            )
          }
          preferences={notes.length > 0 && addNewNoteElement}
          header={showNewNote && newNoteElement}
        />
      </ExpandableSection>
      {!showAllNotes && notes.length > MAX_NOTES_DISPLAY && moreNotesElement}
    </div>
  );
};
export default ContentNotesDetails;
